import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshAccessToken } from 'api';
import { useCheckSignedIn } from 'helpers/useCheckSignedIn';
import { useIdleTimer } from 'react-idle-timer';
import { toast } from 'react-toastify';
import Countdown, { zeroPad } from 'react-countdown';
import {
  timeoutPrompt,
  selectSessionTime,
  selectTimeoutPrompt,
} from 'reducers/auth';
import { signOut, setSessionTime, setIsSignedOut } from 'reducers/auth';
import { loadTokenResource } from './auth';
import { store } from 'index';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

// const signOut = () =>
//   signOutCreator("Your session has expired. Please sign in again.");

let timer: any;

/**
 * Custom Hook to track idle user session and prompt user to continue
 * or sign them out upon session expiry.
 *
 * Makes use of react-idle-timer and react-toastify and requires integration with
 * the project auth state.
 */
export function useTimeoutPrompt() {
  const { t } = useTranslation('Timeout+Prompt', { i18n });
  const isSignedIn = useCheckSignedIn();
  let { sessionTime, sessionRefreshFlag } = useSelector(selectSessionTime);
  //sessionTime = 20000;

  const [backupSessionTime, setBackupSessionTime] = React.useState<
    number | null
  >(null);
  React.useEffect(() => {
    if (sessionTime === 0) {
      const resource = loadTokenResource();
      const data = {
        accessTokenExpires: resource?.accessTokenExpires,
        refreshTokenExpires: resource?.refreshTokenExpires,
      };

      if (data.accessTokenExpires) {
        const now = new Date();
        const sessionExpiry =
          new Date(data.accessTokenExpires).getTime() - now.getTime();

        setBackupSessionTime(sessionExpiry);
        store.dispatch(setSessionTime(sessionExpiry));
      }
    } else {
      setBackupSessionTime(null);
    }
  }, [sessionTime]);
  const [sessionTimeout, setSessionTimeout] = React.useState(
    sessionTime ? sessionTime : backupSessionTime ? backupSessionTime : 0
  );
  const dispatch = useDispatch();
  const continueSessionClick = React.useRef(false);

  const TIMEOUT_THRESHOLD = 80; // prompt user when given % of expiry time reached
  const TIMEOUT_REMAINDER = 100 - TIMEOUT_THRESHOLD; // remaining % of time until expiry

  const MIN_NOTIFY_TIME = 20 * 1000; // 20 seconds is the smallest time left until auto signout for the pop-up to appear

  const IDLE_CHECK_TIME_MAX_LIMIT = sessionTime
    ? sessionTime * (TIMEOUT_THRESHOLD / 100)
    : 1000 * 60; // Maximum value in milliseconds that the IDLE_CHECK_TIME can equal. Must not exceed the TIMEOUT_REMAINDER of the sessionTime (milliseconds)

  const tempIdleCheckTime =
    sessionTime || backupSessionTime
      ? (sessionTime
          ? sessionTime
          : backupSessionTime
          ? backupSessionTime
          : 0 * (TIMEOUT_THRESHOLD / 100)) / 10
      : 10 * 1000; // default to 10 seconds

  const IDLE_CHECK_TIME =
    tempIdleCheckTime < IDLE_CHECK_TIME_MAX_LIMIT && tempIdleCheckTime > 0
      ? tempIdleCheckTime
      : IDLE_CHECK_TIME_MAX_LIMIT; // How often we check if the user is idle and determine what to do (milliseconds)

  const { reset, pause, isIdle } = useIdleTimer({
    timeout: IDLE_CHECK_TIME,
    debounce: 500,
    crossTab: true,
  });

  const bIdle = isIdle(); // If user is idle

  const dismissTimeout = () => {
    dispatch(timeoutPrompt(false));

    reset();
  };

  const Continue = ({ closeToast }: any) => {
    const renderer = ({ hours, minutes, seconds }: any) => {
      return (
        <span>
          {' '}
          {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
        </span>
      );
    };

    const handleClick = async () => {
      /* 
        Whenever the closeToast callback or dismiss method is called the 
        onClose callback is also fired so the below Ref is used to detect 
        that the Continue button has been clicked and stop the onClose 
        callback dispatching an unwanted signOut event 
      */
      continueSessionClick.current = true;

      try {
        await refreshAccessToken();
        closeToast();
        dismissTimeout();
      } catch (error) {}
    };

    return (
      <div className="ToastContent TimeoutPrompt" onClick={handleClick}>
        <h4>{t('SSCandidate_Timeout_Prompt_Are_You_Still_There')}</h4>
        <p>
          {t('SSCandidate_Timeout_Prompt_Session_Timeout_In')}
          <Countdown date={Date.now() + sessionTimeout} renderer={renderer} />
        </p>
        <p>{t('SSCandidate_Timeout_Prompt_Remain_Signed_In')}</p>
        <button className="Btn BtnFull BtnSmall">{t('SSCandidate_Timeout_Prompt_Continue')}</button>
      </div>
    );
  };

  const bTimeoutPrompt = useSelector(selectTimeoutPrompt);

  const notify = () =>
    toast(<Continue />, {
      position: 'bottom-right',
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: true,
      closeButton: false,
      pauseOnHover: false,
      draggable: false,
      onClose: () => {
        if (!continueSessionClick.current) {
          clearTimeout(timer);
          dismissTimeout();
          dispatch(setIsSignedOut(true));
          dispatch(signOut());
        }
        continueSessionClick.current = false;
      },
    });

  React.useEffect(() => {
    if (
      bIdle &&
      (sessionTimeout <= Math.ceil((sessionTime / 100) * TIMEOUT_REMAINDER) ||
        sessionTimeout <= MIN_NOTIFY_TIME) &&
      isSignedIn
    ) {
      pause();

      if (!bTimeoutPrompt && sessionTimeout > 0) {
        dispatch(timeoutPrompt(true));
        notify();
      }
    }
  }, [sessionTimeout]);

  React.useEffect(() => {
    if (isSignedIn) {
      reset();
    }
    if (!isSignedIn) {
      pause();
    }
  }, [isSignedIn]);

  React.useEffect(() => {
    setSessionTimeout(sessionTime);
    if (isSignedIn && sessionTime > 0) {
      timer = setInterval(() => {
        setSessionTimeout((sessionTimeout) => sessionTimeout - IDLE_CHECK_TIME);
      }, IDLE_CHECK_TIME);
    }

    return () => clearTimeout(timer);
  }, [sessionTime, sessionRefreshFlag, isSignedIn]);

  React.useEffect(() => {
    (async () => {
      if (
        !bIdle &&
        (sessionTimeout <= Math.ceil((sessionTime / 100) * TIMEOUT_REMAINDER) ||
          sessionTimeout <= MIN_NOTIFY_TIME) &&
        isSignedIn
      ) {
        try {
          await refreshAccessToken();
        } catch (error) {}
      }

      if (sessionTimeout < 0) {
        // Not <= 0, as sessionTimeout = 0 on initial render
        toast.dismiss();
        dispatch(signOut());
      }
    })();
  }, [sessionTimeout]);
}
